// THE "MOST COMMON PATH" (the red line on the flow lines): its shape and the words that explain it. The ROUTE itself is not worked out here any more (2026-10-25): there is one definition of it, in
// insights/pathCore.ts, shared with the Insights card, and happyRoute.ts turns that route into this shape (pages as boxes). What is left here is the data the line is drawn from and the sentences
// that say, in what visits DID, where it starts, where it goes and why it stops.
// Copy rule (docs/MESSAGING.md): Skydive shows BEHAVIOUR, people infer CAUSE. The words say what visits DID ("more visits ended here than went on"), never why.
import { escapeHtml } from "./visitorsFormat";

export type EndReason = "no-next" | "thin" | "complete" | "max";

export interface HappyPath {
  steps: number[]; // box indices, in order
  startWeight: number; // visits that landed on the first page
  totalArrivals: number; // visits that landed anywhere counted
  hops: Array<{ from: number; to: number; weight: number }>;
  end: { index: number; departed: number; nextWeight: number; nextIndex: number | null; reason: EndReason };
}

const n = (value: number): string => Math.round(value).toLocaleString("en-US");
const visits = (value: number): string => `${n(value)} ${Math.round(value) === 1 ? "visit" : "visits"}`;

// WHY THE ROUTE ENDS WHERE IT DOES, in words that say what visits DID (never why they did it). `name` turns a box index into the text to show: escaped for the legacy rows below, plain for the
// tooltip builder (flowTooltips.ts), which escapes the whole sentence itself.
const whyItEnds = (path: HappyPath, name: (index: number) => string): string => {
  const { end } = path;
  if (end.reason === "no-next") {
    return `No visit went on from ${name(end.index)} to a page not already on this path${end.departed > 0 ? `, and ${visits(end.departed)} ended there` : ""}`;
  }
  if (end.reason === "thin") {
    return `After ${name(end.index)}, too few visits went on to say where most go next`;
  }
  if (end.reason === "max") {
    return `The path is cut off after ${path.steps.length} stops`;
  }
  return "It has used every page it can; the route is as long as the data allows";
};

/** what the line is counted from, as a phrase the caller built ("people's visits in Last 30 Days", "visits that ended so far (playback up to 3:55 PM)") */
const basedOn = (coverage: string): string => coverage || "the visits counted";

// The explanation shown when the line is hovered: label / text rows (the legacy form: `LABEL : text`). Every page name is escaped: names come from visitors' own URLs.
// `coverage` says what the numbers cover, e.g. "playback up to 3:55 PM".
export const describeHappyPath = (path: HappyPath, label: (index: number) => string, coverage: string): Array<[string, string]> => {
  const name = (index: number) => escapeHtml(label(index));
  const rows: Array<[string, string]> = [];
  rows.push(["Most common path", path.steps.map(name).join(" → ")]);
  const share = path.totalArrivals > 0 ? Math.round((path.startWeight / path.totalArrivals) * 100) : 0;
  rows.push(["Starts at", `${name(path.steps[0])}, where ${n(path.startWeight)} of ${n(path.totalArrivals)} visits began (${share}%)`]);
  path.hops.forEach((hop, i) => {
    rows.push([i === 0 ? "Then" : `Then (${i + 1})`, `${name(hop.to)}: ${visits(hop.weight)} went from ${name(hop.from)} to ${name(hop.to)}`]);
  });
  rows.push(["Ends at", name(path.end.index)]);
  rows.push(["Why it ends here", whyItEnds(path, name)]);
  rows.push(["Based on", basedOn(coverage)]);

  return rows;
};

/** The same facts as plain data, for the tooltip (flowTooltips.ts). Names are NOT escaped here: the HTML builder escapes everything it prints. */
export interface HappyPathFacts {
  start: string;
  end: string;
  /** visits that landed on the first page, of all visits that landed anywhere counted, and the share (whole percent) */
  began: { weight: number; total: number; pct: number };
  hops: Array<{ from: string; to: string; weight: number }>;
  why: string;
  basedOn: string;
}

export const happyPathFacts = (path: HappyPath, label: (index: number) => string, coverage: string): HappyPathFacts => ({
  start: label(path.steps[0]),
  end: label(path.end.index),
  began: { weight: path.startWeight, total: path.totalArrivals, pct: path.totalArrivals > 0 ? Math.round((path.startWeight / path.totalArrivals) * 100) : 0 },
  hops: path.hops.map((hop) => ({ from: label(hop.from), to: label(hop.to), weight: hop.weight })),
  why: whyItEnds(path, label),
  basedOn: basedOn(coverage),
});
